import type { GetMyPassengerResponse } from '@massilia/queries'

import {
  CompanyAutocompleteField,
  CountryAutocompleteField,
  DEFAULT_OPTION,
  FieldError,
  NAME_TOOLTIP_TEXT,
  NationalityAutocompleteField,
  withFieldGroup,
} from '@massilia/features'
import { Button, Checkbox, Name } from '@massilia/ui'
import { CreditCard, FileText, Plus, Trash2 } from 'lucide-react'

import type { InformationsPassengerSchema } from '@/lib/schemas/informations'

import { DOCUMENT_TYPE_OPTIONS, GENDER_OPTIONS } from '../-utils/constants'
import { BirthDateSelectFields } from './birth-date-select-fields'

interface PassengerFieldGroupProps {
  index: number
  label?: string
  savedPassengers?: GetMyPassengerResponse[]
}

export const PassengerFieldGroup = withFieldGroup({
  defaultValues: {} as InformationsPassengerSchema,
  props: {} as PassengerFieldGroupProps,
  render: function PassengerFields({ group, index, label, savedPassengers }) {
    const hasSavedPassengers = savedPassengers && savedPassengers.length > 0

    function handleSelectSavedPassenger(passengerId: string) {
      const passenger = savedPassengers?.find((p) => p.id === passengerId)
      if (!passenger) return

      const birthDate = new Date(passenger.birthDate)
      group.setFieldValue('firstName', passenger.firstName)
      group.setFieldValue('lastName', passenger.lastName)
      group.setFieldValue('gender', passenger.gender)
      group.setFieldValue('birthDay', String(birthDate.getDate()).padStart(2, '0'))
      group.setFieldValue('birthMonth', String(birthDate.getMonth() + 1).padStart(2, '0'))
      group.setFieldValue('birthYear', String(birthDate.getFullYear()))
      group.setFieldValue('nationality', {
        label: passenger.nationalityRef?.nationalityFr ?? passenger.nationalityCode,
        value: passenger.nationalityCode,
      })
      group.setFieldValue('retrieveFromSaved', true)
      group.setFieldValue('savedPassengerId', passengerId)
    }

    function handleToggleRetrieve(checked: boolean) {
      if (checked) {
        group.setFieldValue('retrieveFromSaved', true)
      } else {
        group.setFieldValue('firstName', '')
        group.setFieldValue('lastName', '')
        group.setFieldValue('gender', '' as 'FEMALE' | 'MALE')
        group.setFieldValue('birthDay', '')
        group.setFieldValue('birthMonth', '')
        group.setFieldValue('birthYear', '')
        group.setFieldValue('nationality', DEFAULT_OPTION)
        group.setFieldValue('fidelityCard', undefined)
        group.setFieldValue('identityDocument', undefined)
        group.setFieldValue('retrieveFromSaved', false)
        group.setFieldValue('savedPassengerId', null)
      }
    }

    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium text-gray-light-700">
          {label ?? `Voyageur ${index + 1}`}
        </p>
        <div className="flex flex-col gap-6 rounded-xl border border-gray-light-200 p-4">
          {/* Retrieve saved passenger */}
          {hasSavedPassengers && (
            <group.Subscribe selector={(state) => state.values.retrieveFromSaved}>
              {(retrieveFromSaved) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={retrieveFromSaved}
                      onCheckedChange={(checked) => {
                        handleToggleRetrieve(checked === true)
                      }}
                    />
                    <span className="text-sm font-medium text-gray-light-900">
                      Récupérer les informations voyageurs
                    </span>
                  </div>
                  {retrieveFromSaved && (
                    <group.AppField name="savedPassengerId">
                      {(field) => (
                        <field.SelectField
                          label="Sélectionner un voyageur"
                          options={savedPassengers.map((p) => ({
                            label: `${p.firstName} ${p.lastName}`,
                            renderOption: (option) => <Name>{option.label}</Name>,
                            value: p.id,
                          }))}
                          renderValue={(option) => <Name>{option?.label}</Name>}
                          onValueChange={(value) => {
                            handleSelectSavedPassenger(value)
                          }}
                        />
                      )}
                    </group.AppField>
                  )}
                </div>
              )}
            </group.Subscribe>
          )}

          {/* Gender */}
          <group.AppField name="gender">
            {(field) => (
              <field.RadioGroupField
                orientation="horizontal"
                options={GENDER_OPTIONS}
                label="Sexe"
              />
            )}
          </group.AppField>

          {/* Names */}
          <div className="flex flex-col gap-4 md:flex-row">
            <group.AppField name="firstName">
              {(field) => (
                <field.TextField label="Prénoms (maximum 2)" questionToolTip={NAME_TOOLTIP_TEXT} />
              )}
            </group.AppField>

            <group.AppField name="lastName">
              {(field) => <field.TextField label="Nom(s)" questionToolTip={NAME_TOOLTIP_TEXT} />}
            </group.AppField>
          </div>

          {/* Birth date */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <group.AppField name="birthDay">
                {(dayField) => (
                  <group.AppField name="birthMonth">
                    {(monthField) => (
                      <group.AppField name="birthYear">
                        {(yearField) => (
                          <>
                            <BirthDateSelectFields
                              birthDay={dayField.state.value}
                              birthMonth={monthField.state.value}
                              birthYear={yearField.state.value}
                              onDayChange={(v) => {
                                dayField.handleChange(v)
                              }}
                              onMonthChange={(v) => {
                                monthField.handleChange(v)
                              }}
                              onYearChange={(v) => {
                                yearField.handleChange(v)
                              }}
                            />
                          </>
                        )}
                      </group.AppField>
                    )}
                  </group.AppField>
                )}
              </group.AppField>
            </div>
            <group.AppField name="birthDay">
              {(field) => <FieldError errors={field.state.meta.errors} />}
            </group.AppField>
            <group.AppField name="birthMonth">
              {(field) => <FieldError errors={field.state.meta.errors} />}
            </group.AppField>
            <group.AppField name="birthYear">
              {(field) => <FieldError errors={field.state.meta.errors} />}
            </group.AppField>
          </div>

          {/* Nationality */}
          <group.AppField name="nationality">
            {() => <NationalityAutocompleteField label="Nationalité" />}
          </group.AppField>

          {/* Identity Document */}
          <group.Subscribe selector={(state) => state.values.identityDocument}>
            {(identityDocument) =>
              identityDocument ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <FileText className="size-3.5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">
                        Document d&apos;identit&eacute;
                      </span>
                    </div>
                    <Button
                      className="h-7 gap-1 text-xs text-red-500 hover:text-red-700"
                      onClick={() => {
                        group.setFieldValue('identityDocument', undefined)
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="size-3.5" />
                      Supprimer
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <group.AppField name="identityDocument.type">
                      {(field) => (
                        <field.SelectField label="Type" options={DOCUMENT_TYPE_OPTIONS} />
                      )}
                    </group.AppField>
                    <group.AppField name="identityDocument.documentNumber">
                      {(field) => <field.TextField label="Num&eacute;ro" placeholder="19DE71227" />}
                    </group.AppField>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <group.AppField name="identityDocument.issuingCountry">
                      {() => <CountryAutocompleteField label="Pays de d&eacute;livrance" />}
                    </group.AppField>
                    <group.AppField name="identityDocument.expiryDate">
                      {(field) => <field.DateField label="Expiration" />}
                    </group.AppField>
                  </div>
                </div>
              ) : (
                <Button
                  className="h-8 gap-1.5 self-start text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    group.setFieldValue('identityDocument', {
                      documentNumber: '',
                      expiryDate: '',
                      issuingCountry: DEFAULT_OPTION,
                      type: 'PASSPORT' as const,
                    })
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Plus className="size-3.5" />
                  Ajouter un document d&apos;identit&eacute;
                </Button>
              )
            }
          </group.Subscribe>

          {/* Fidelity Card */}
          <group.Subscribe selector={(state) => state.values.fidelityCard}>
            {(fidelityCard) =>
              fidelityCard ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <CreditCard className="size-3.5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">
                        Carte de fid&eacute;lit&eacute;
                      </span>
                    </div>
                    <Button
                      className="h-7 gap-1 text-xs text-red-500 hover:text-red-700"
                      onClick={() => {
                        group.setFieldValue('fidelityCard', undefined)
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="size-3.5" />
                      Supprimer
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <group.AppField name="fidelityCard.companyName">
                      {() => <CompanyAutocompleteField label="Compagnie" />}
                    </group.AppField>
                    <group.AppField name="fidelityCard.cardNumber">
                      {(field) => (
                        <field.TextField label="Num&eacute;ro de carte" placeholder="12987654321" />
                      )}
                    </group.AppField>
                  </div>
                </div>
              ) : (
                <Button
                  className="h-8 gap-1.5 self-start text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    group.setFieldValue('fidelityCard', {
                      companyName: DEFAULT_OPTION,
                      cardNumber: '',
                    })
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Plus className="size-3.5" />
                  Ajouter une carte de fid&eacute;lit&eacute;
                </Button>
              )
            }
          </group.Subscribe>
        </div>
      </div>
    )
  },
})
